import React from 'react';
import { AlertCircle, AlertTriangle, Info, RefreshCw, MessageCircle, X, Tag } from 'lucide-react';
import { formatErrorForDisplay, canRetryPayment, shouldContactSupport } from '../../utils/paymentErrorHandler';
import { useTranslation } from 'react-i18next';

interface PaymentErrorDisplayProps {
  error: any;
  onRetry?: () => void;
  onContactSupport?: () => void;
  onDismiss?: () => void;
  onRemovePromoCode?: () => void;
  isRetrying?: boolean;
  className?: string;
}

export default function PaymentErrorDisplay({
  error,
  onRetry,
  onContactSupport,
  onDismiss,
  onRemovePromoCode,
  isRetrying = false,
  className = ''
}: PaymentErrorDisplayProps) {
  if (!error) return null;

  const { t } = useTranslation();
  const errorInfo = formatErrorForDisplay(error);
  const canRetry = canRetryPayment(error);
  const needsSupport = shouldContactSupport(error);
  
  // Check if this is a promotion code related error
  const isPromotionCodeError = error?.message?.toLowerCase().includes('promotion code cannot be redeemed') && 
                              error?.message?.toLowerCase().includes('prior transactions');

  // Get appropriate icon based on severity
  const getIcon = () => {
    switch (errorInfo.severity) {
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
  };

  // Get border and background colors based on severity
  const getColorClasses = () => {
    switch (errorInfo.severity) {
      case 'error':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-amber-200 bg-amber-50';
      case 'info':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-red-200 bg-red-50';
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${getColorClasses()} ${className}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        
        <div className="flex-grow min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-grow">
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                {errorInfo.title}
              </h3>
              
              <p className="text-sm text-gray-700 mb-2">
                {errorInfo.message}
              </p>
              
              <p className="text-sm text-gray-600 mb-3">
                {errorInfo.action}
              </p>
            </div>
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Dismiss error"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Remove Promotion Code button for promotion code errors */}
            {isPromotionCodeError && onRemovePromoCode && (
              <button
                onClick={onRemovePromoCode}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
              >
                <Tag className="w-3 h-3 mr-1" />
                {t('subscription.paymentErrors.removePromotionCode')}
              </button>
            )}
            
            {canRetry && onRetry && (
              <button
                onClick={onRetry}
                disabled={isRetrying}
                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  errorInfo.severity === 'error'
                    ? 'bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50'
                    : errorInfo.severity === 'warning'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50'
                }`}
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    {t('common.retrying')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    {t('common.tryAgain')}
                  </>
                )}
              </button>
            )}
            
            {needsSupport && onContactSupport && (
              <button
                onClick={onContactSupport}
                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  errorInfo.severity === 'error'
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : errorInfo.severity === 'warning'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <MessageCircle className="w-3 h-3 mr-1" />
                {t('common.support')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 